import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowRight,
  lucideCheck,
  lucideChevronRight,
  lucideCircleCheck,
  lucidePartyPopper,
  lucideX,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmProgressImports } from '@spartan-ng/helm/progress';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CompanyService, Company } from '../../services/company.service';
import { VoucherProductService } from '../../services/voucher-product.service';
import { LoggerService } from '../../services/logger.service';
import { SetupTask, ALL_SETUP_TASKS } from '../../config/setup-tasks.config';

export type { SetupTask };

@Component({
  selector: 'app-setup-checklist',
  standalone: true,
  imports: [
    RouterModule,
    NgIcon,
    HlmAlertImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmItemImports,
    HlmProgressImports,
  ],
  providers: [
    provideIcons({
      lucideArrowRight,
      lucideCheck,
      lucideChevronRight,
      lucideCircleCheck,
      lucidePartyPopper,
      lucideX,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './setup-checklist.component.html',
})
export class SetupChecklistComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  private readonly BANNER_COOKIE = 'setup_done_banner_dismissed';
  private readonly CONGRATS_COOKIE = 'setup_required_done_banner_dismissed';

  // Component state lives in signals so OnPush stays correct: every getter below
  // reads them, and the template tracks those reads.
  private readonly _dismissingTaskId = signal<string | null>(null);
  private readonly _skippedSectionExpanded = signal(false);
  private readonly _overviewHidden = signal(false);
  private readonly _congratsBannerDismissed = signal(false);

  /** Read straight from the store — no subscription, no local copy. */
  get company(): Company | null {
    return this.companyService.company();
  }
  get productCount(): number {
    return this.productService.products().length;
  }
  get dismissingTaskId(): string | null {
    return this._dismissingTaskId();
  }
  get skippedSectionExpanded(): boolean {
    return this._skippedSectionExpanded();
  }
  get congratsBannerDismissed(): boolean {
    return this._congratsBannerDismissed();
  }

  private readonly route = inject(ActivatedRoute);

  constructor(
    private companyService: CompanyService,
    private productService: VoucherProductService,
    private logger: LoggerService,
  ) {}

  ngOnInit() {
    const cookies = document.cookie.split(';');
    this._overviewHidden.set(cookies.some((c) => c.trim().startsWith(`${this.BANNER_COOKIE}=1`)));
    this._congratsBannerDismissed.set(
      cookies.some((c) => c.trim().startsWith(`${this.CONGRATS_COOKIE}=1`)),
    );

    // Arriving from the notification popover: asking for the overview is an
    // explicit request to see it, so un-hide it, bring it into view and flag it.
    this.route.fragment.pipe(takeUntil(this.destroy$)).subscribe((fragment) => {
      if (fragment !== 'setup-checklist') return;
      this.showOverview();
      setTimeout(() => this.highlightCard(), 350);
    });
  }

  private highlightCard() {
    const el = document.getElementById('setup-checklist');
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('setting-highlight');
    setTimeout(() => {
      el.classList.remove('setting-highlight');
      el.classList.add('setting-highlight-fade');
      setTimeout(() => el.classList.remove('setting-highlight-fade'), 800);
    }, 2800);
  }

  private visibleTasks(): SetupTask[] {
    if (!this.company) return [];
    return ALL_SETUP_TASKS.filter((t) => !t.isVisible || t.isVisible(this.company!));
  }

  private storedIds(): string[] {
    return this.company?.dismissed_setup_tasks ?? [];
  }

  private isManuallyDone(taskId: string): boolean {
    return this.storedIds().includes(`done:${taskId}`);
  }

  private isSkipped(taskId: string): boolean {
    return this.storedIds().includes(taskId);
  }

  private isTaskDone(t: SetupTask): boolean {
    return t.isCompleted(this.company!, this.productCount) || this.isManuallyDone(t.id);
  }

  // ── Phase 1: required tasks ──────────────────────────────────────────────

  get requiredTasks(): SetupTask[] {
    return this.visibleTasks().filter((t) => t.required);
  }

  get activeRequiredTasks(): SetupTask[] {
    return this.requiredTasks.filter((t) => !this.isTaskDone(t));
  }

  get completedRequiredTasks(): SetupTask[] {
    return this.requiredTasks.filter((t) => this.isTaskDone(t));
  }

  /** True while any required task is still open — drives which phase is shown. */
  get requiredPhase(): boolean {
    return this.activeRequiredTasks.length > 0;
  }

  get requiredProgressPercent(): number {
    const total = this.requiredTasks.length;
    if (!total) return 100;
    return Math.round((this.completedRequiredTasks.length / total) * 100);
  }

  // ── Phase 2: optional tasks (shown only after required are done) ─────────

  get activeTasks(): SetupTask[] {
    if (!this.company) return [];
    return this.visibleTasks().filter(
      (t) => !t.required && !this.isSkipped(t.id) && !this.isTaskDone(t),
    );
  }

  get completedTasks(): SetupTask[] {
    if (!this.company) return [];
    return this.visibleTasks().filter((t) => this.isTaskDone(t));
  }

  get dismissedTasks(): SetupTask[] {
    if (!this.company) return [];
    return this.visibleTasks().filter(
      (t) => !t.required && this.isSkipped(t.id) && !this.isTaskDone(t),
    );
  }

  /**
   * The user may hide the card whenever the shop can actually sell. A new
   * required task always forces it back — at that point the shop is blocked and
   * the card is no longer optional reading. Everything else is reachable again
   * via the notification popover, which un-hides the card on the way in.
   */
  get isHidden(): boolean {
    return this._overviewHidden() && !this.requiredPhase;
  }

  /** Hiding is offered once nothing blocks selling any more. */
  get canHide(): boolean {
    return !this.requiredPhase;
  }

  get allDone(): boolean {
    return this.activeTasks.length === 0;
  }

  get progressPercent(): number {
    const visible = this.visibleTasks();
    if (!visible.length) return 0;
    const done = visible.filter((t) => this.isTaskDone(t) || this.isSkipped(t.id)).length;
    return Math.round((done / visible.length) * 100);
  }

  // ── What the current phase shows ─────────────────────────────────────────

  get headline(): string {
    return this.requiredPhase ? 'Fast startklar!' : 'Noch mehr rausholen';
  }

  get subline(): string {
    if (this.requiredPhase) {
      const open = this.activeRequiredTasks.length;
      // A member doesn't sell: what it waits for is its payout.
      const goal = this.company?.is_member
        ? 'kann dir dein Netzwerk deine Einlösungen überweisen'
        : 'kannst du Gutscheine verkaufen';
      return open === 1
        ? `Nur noch ein Schritt, dann ${goal}.`
        : `Noch ${open} Schritte, dann ${goal}.`;
    }
    if (this.allDone) return 'Dein Gutschein-System ist vollständig eingerichtet.';
    return this.company?.is_member
      ? 'Optionale Schritte, mit denen mehr Kunden die Gutscheine deines Netzwerks bei dir einlösen.'
      : 'Optionale Schritte, mit denen du mehr aus deinem Shop herausholst.';
  }

  /** The task list of the current phase: required first, optional afterwards. */
  get currentTasks(): SetupTask[] {
    return this.requiredPhase ? this.activeRequiredTasks : this.activeTasks;
  }

  get currentPercent(): number {
    return this.requiredPhase ? this.requiredProgressPercent : this.progressPercent;
  }

  /** Completed tasks of the current phase, listed struck through below. */
  get currentCompletedTasks(): SetupTask[] {
    return this.requiredPhase ? this.completedRequiredTasks : this.completedTasks;
  }

  markDone(taskId: string) {
    if (this._dismissingTaskId()) return;
    this._dismissingTaskId.set(taskId);
    this.companyService.dismissSetupTask(`done:${taskId}`).subscribe({
      next: () => {
        this._dismissingTaskId.set(null);
      },
      error: (err) => {
        this.logger.error('Failed to mark task as done:', err);
        this._dismissingTaskId.set(null);
      },
    });
  }

  dismissTask(taskId: string) {
    if (this._dismissingTaskId()) return;
    this._dismissingTaskId.set(taskId);
    this.companyService.dismissSetupTask(taskId).subscribe({
      next: () => {
        this._dismissingTaskId.set(null);
      },
      error: (err) => {
        this.logger.error('Failed to dismiss setup task:', err);
        this._dismissingTaskId.set(null);
      },
    });
  }

  hideOverview() {
    this._overviewHidden.set(true);
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `${this.BANNER_COOKIE}=1; expires=${expires.toUTCString()}; path=/`;
  }

  private showOverview() {
    if (!this._overviewHidden()) return;
    this._overviewHidden.set(false);
    document.cookie = `${this.BANNER_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }

  dismissCongratsBanner() {
    this._congratsBannerDismissed.set(true);
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `${this.CONGRATS_COOKIE}=1; expires=${expires.toUTCString()}; path=/`;
  }

  toggleSkipped() {
    this._skippedSectionExpanded.update((open) => !open);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
